// App
import type React from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// Components
import VisuallyHidden from 'app/shared/modules/VisuallyHidden';

// Hooks
import useFavoriteButtonAnimation from 'app/shared/modules/FavoriteButton/useFavoriteButtonAnimation';

// Misc / utils
import IconHeartFilledV3 from 'images/icons/save-icon-filled.svg';
import IconHeartDarkFilledV3 from 'images/icons/save-icon-dark-filled.svg';
import IconHeart from 'images/icons/heart-outline-white.min.svg';
import IconHeartFilled from 'images/icons/heart-fill-gold.min.svg';
import { getFavoriteButtonCX, getHeartIconAndAnimationCX } from './styles';
import type { RootReduxState } from 'app/shared/flux/reducers';
import { noop } from 'lodash';
import type { ListingDetails } from 'app/types/listingDetails.type';

type FavoriteButtonProps = {
  onClick?: (e: React.MouseEvent) => void;
  listing?: ListingDetails;
  isBiggerListingCard?: boolean;
  alreadyFavorited?: boolean;
  size?: string;
};

const FavoriteButton = ({
  onClick = noop,
  listing = {} as ListingDetails,
  isBiggerListingCard = false,
  alreadyFavorited = false,
  size = '24px',
}: FavoriteButtonProps) => {
  // Redux
  const isLoggedIn = useSelector((state: RootReduxState) => state.user.loggedIn) || false;

  // React
  const [handleAnimation, pulse] = useFavoriteButtonAnimation({ listing, isLoggedIn });

  const handleFavoriteSelect = useCallback(
    (e: React.MouseEvent) => {
      onClick(e);
      handleAnimation();
    },
    [handleAnimation, onClick],
  );

  if (!listing) {
    return null;
  }

  const heartIcon = isBiggerListingCard ? IconHeartDarkFilledV3 : IconHeart;
  const heartIconFilled = isBiggerListingCard ? IconHeartFilledV3 : IconHeartFilled;

  return (
    <button
      aria-label={`Add ${listing.displayName} to favorites`}
      aria-pressed={alreadyFavorited}
      className={getFavoriteButtonCX(isBiggerListingCard)}
      data-name="FavoriteButton"
      onClick={handleFavoriteSelect}
    >
      <img
        src={alreadyFavorited ? heartIconFilled : heartIcon}
        onAnimationEnd={handleAnimation}
        className={getHeartIconAndAnimationCX(pulse)}
        data-name="HeartIcon"
        height={size}
        width={size}
        alt=""
      />
      <VisuallyHidden>Favorite button</VisuallyHidden>
    </button>
  );
};

FavoriteButton.propTypes = {
  alreadyFavorited: PropTypes.bool,
  isBiggerListingCard: PropTypes.bool,
  listing: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  size: PropTypes.string,
};

export default FavoriteButton;
